import React, { useEffect, useState } from "react";
import { Button, Col, Row, Spinner } from "react-bootstrap";
import { fetchCaseRange, fetchReportGoals, fetchReportTasks } from "../../services/httpClient";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import html2canvas from "html2canvas";
import ReportTimeInterval from "./ui/ReportTimeInterval";
import { GoalsCompletedMetric } from "./charts-metrics/GoalsCompletedMetric";
import { TasksCompletedMetric } from "./charts-metrics/TasksCompletedMetric";
import { formatDateTimeToLocal } from "../../helpers/date";
import { GoalsOutstandingMetric } from "./charts-metrics/GoalsOutstandingMetric";
import { GoalsCompletedPercentageMetric } from "./charts-metrics/GoalsCompletedPercentageMetric";
import DataTable from "./ui/DataTable";

const GoalsAndTasksTab = ({ buildPDF, canvas, setCanvas, iPad }: any) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const [dateInterval, setDateInterval] = useState<number>(365);
  const [selectedData, setSelectedData] = useState<any[] | null>(null);
  const [dataHeaders, setDataHeaders] = useState<any[] | null>();
  const [activeCard, setActiveCard] = useState<string>("");

  const [caseRange, setCaseRange] = useState<any>([]);
  const [casesCompletingGoals, setCasesCompletingGoals] = useState<any>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [goals, setGoals] = useState<any>([]);
  const [goalsOutstanding, setGoalsOutstanding] = useState<any>([]);
  const [goalsCompleted, setGoalsCompleted] = useState<any>([]);
  const [taskData, setTaskData] = useState<any>([]);
  const [reportRunning, setReportRunning] = useState<boolean>(false);

  useEffect(() => {
    const start = new Date();
    start.setDate(start.getDate() - dateInterval);
    const startString = start.toISOString().slice(0, 10);
    const endString = new Date().toISOString().slice(0, 10);
    setStartDate(startString);
    setEndDate(endString);
    getData(startString, endString);
  }, []);

  useEffect(() => {
    if (reportRunning) {
      buildPDF();
      setReportRunning(false);
    }
  }, [canvas]);

  const getData = (start: string, end: string) => {
    getCaseRange(start, end);
    getGoalsAndTasks(start, end);
  };

  const getCaseRange = (start: string, end: string) => {
    fetchCaseRange(start, end)
      .then((res: any) => {
        const cases = res[0].data;
        setCaseRange(cases);
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getGoalsAndTasks = (start: string, end: string) => {
    fetchReportGoals(start, end)
      .then((res: any) => {
        const goals = res[0].data;
        const sortedGoals = goals.sort((a: any, b: any) => (a.completed_at < b.completed_at ? 1 : -1));
        sortedGoals.forEach((goal: any) => {
          if (goal?.completed_at) {
            const localDateTime = formatDateTimeToLocal(goal.completed_at);
            goal.completed_at_formatted = localDateTime.date + " " + localDateTime.time;
          }
          if (goal?.created_at) {
            const localDateTime = formatDateTimeToLocal(goal.created_at);
            goal.created_at_formatted = localDateTime.date + " " + localDateTime.time;
          }
        });
        setGoals(sortedGoals);

        //all goals outstanding at end of time range
        //that means they were created before end date and not yet completed
        const outstanding = sortedGoals.filter((g: any) => !g.completed_at);
        setGoalsOutstanding(outstanding);

        //all goals completed during time range
        const completed = sortedGoals.filter((g: any) => g.completed_at);
        setGoalsCompleted(completed);

        const completingCases = uniqueCasesWithCompletedGoals(sortedGoals);
        setCasesCompletingGoals(completingCases);

        fetchReportTasks(start, end).then((res: any) => {
          const tasks = res[0].data;
          const sortedTasks = tasks.sort((a: any, b: any) => (a.cm_reviewed_at < b.cm_reviewed_at ? 1 : -1));
          sortedTasks.forEach((task: any) => {
            const localDateTime = formatDateTimeToLocal(task.cm_reviewed_at);
            task.cm_reviewed_at_formatted = localDateTime.date + " " + localDateTime.time;
          });
          setTaskData(sortedTasks);
        });
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setActiveCard("");
        setIsLoading(false);
      });
  };

  const uniqueCasesWithCompletedGoals = (sortedGoals: any[]) =>
    Object.values(
      sortedGoals
        .filter((g: any) => g.completed_at)
        .reduce((acc: any, g: any) => {
          if (acc[g.case_id]) {
            acc[g.case_id].goals_completed++;
          } else {
            acc[g.case_id] = { case_id: g.case_id, case_name: g.case_name, goals_completed: 1 };
          }
          return acc;
        }, {}),
    );

  const downloadReport = async () => {
    setReportRunning(true);
    const reportSection = document.getElementById("goals-section");
    const reportTitle = document.getElementById("goals-title");
    const refreshButton = document.getElementById("goals-refresh-button");
    const downloadGoalsButton = document.getElementById("download-goals-button");
    const downloadTasksButton = document.getElementById("download-tasks-button");

    if (reportSection) {
      if (reportTitle) {
        reportTitle.style.visibility = "hidden";
        reportTitle.style.display = "none";
      }
      if (refreshButton) refreshButton.style.visibility = "hidden";
      if (downloadGoalsButton) downloadGoalsButton.style.visibility = "hidden";
      if (downloadTasksButton) downloadTasksButton.style.visibility = "hidden";

      const images = reportSection.getElementsByTagName("img");
      for (let i = 0; i < images.length; i++) {
        images[i].crossOrigin = "anonymous";
      }

      const canvas = await html2canvas(reportSection, { useCORS: true });
      setCanvas(canvas);

      if (reportTitle) {
        reportTitle.style.visibility = "visible";
        reportTitle.style.display = "flex";
      }
      if (refreshButton) refreshButton.style.visibility = "visible";
      if (downloadGoalsButton) downloadGoalsButton.style.visibility = "visible";
      if (downloadTasksButton) downloadTasksButton.style.visibility = "visible";
    } else {
      console.error("Report section not found");
      setReportRunning(false);
    }
  };

  const onClickCard = (cardId: string) => {
    setActiveCard((prev) => (prev === cardId ? "" : cardId));
  };

  return (
    <div id="goals-section">
      {isLoading ? (
        <div className="text-center mt-5">
          <Spinner animation="border" style={{ color: "#F6893D" }} />
        </div>
      ) : (
        <>
          <Row id="goals-title" className="mb-2">
            <Col className="col-8">
              <h4 className="hero-heading app-header-text">Goals and Tasks</h4>
            </Col>
            <Col className="text-end col-4">
              {!iPad && (
                <Button className="app-primary-color button" onClick={() => downloadReport()}>
                  <FontAwesomeIcon icon={faDownload} />
                  {" Report"}
                </Button>
              )}
            </Col>
          </Row>
          <ReportTimeInterval
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            dateInterval={dateInterval}
            setDateInterval={setDateInterval}
            intervalOne={30}
            intervalOneText={"30 Days"}
            intervalTwo={90}
            intervalTwoText={"90 Days"}
            intervalThree={180}
            intervalThreeText={"180 Days"}
            intervalFour={365}
            intervalFourText={"Last Year"}
            onClickRefresh={getData}
            hideBtnId="goals-refresh-button"
          />
          <Row className="mt-3 mb-3">
            <Col className="col-3">
              <GoalsOutstandingMetric
                data={goalsOutstanding}
                readOnly={false}
                setSelectedData={setSelectedData}
                setDataHeaders={setDataHeaders}
                active={activeCard === "Goals Outstanding"}
                onHandleClick={() => onClickCard("Goals Outstanding")}
              />
            </Col>
            <Col className="col-3">
              <GoalsCompletedMetric
                data={goalsCompleted}
                readOnly={false}
                setSelectedData={setSelectedData}
                setDataHeaders={setDataHeaders}
                active={activeCard === "Goals Completed"}
                onHandleClick={() => onClickCard("Goals Completed")}
              />
            </Col>
            <Col className="col-3">
              <TasksCompletedMetric
                data={taskData}
                readOnly={false}
                setSelectedData={setSelectedData}
                setDataHeaders={setDataHeaders}
                active={activeCard === "Tasks Completed"}
                onHandleClick={() => onClickCard("Tasks Completed")}
              />
            </Col>
            <Col className="col-3">
              <GoalsCompletedPercentageMetric
                dataCases={caseRange}
                dataGoals={casesCompletingGoals}
                readOnly={false}
                setSelectedData={setSelectedData}
                setDataHeaders={setDataHeaders}
                active={activeCard === "Cases Completing Goals"}
                onHandleClick={() => onClickCard("Cases Completing Goals")}
              />
            </Col>
          </Row>
          {activeCard &&
            (selectedData && selectedData.length > 0 ? (
              <DataTable tableHeadings={dataHeaders} tableData={selectedData} title={activeCard} />
            ) : (
              <p>{`No ${activeCard} Records`}</p>
            ))}
        </>
      )}
    </div>
  );
};

export default GoalsAndTasksTab;
